/**
 * ProtectedRoute Component
 * Route guard that ensures only authenticated users can access protected routes
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { sessionManager } from '../services/sessionManager';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

/**
 * ProtectedRoute component that guards routes based on authentication status
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/login',
  requireAuth = true
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Verifying authentication..." />
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Store the attempted URL for redirect after login
    sessionManager.setRedirectUrl(location.pathname + location.search);
    
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // If authentication is not required but user is authenticated (e.g., login page)
  if (!requireAuth && isAuthenticated) {
    // Check if there's a stored redirect URL
    const redirectUrl = sessionManager.getAndClearRedirectUrl();
    return <Navigate to={redirectUrl || '/'} replace />;
  }

  // Render the protected content
  return <>{children}</>;
};

/**
 * Higher-order component for route protection
 */
export const withRouteProtection = (
  Component: React.ComponentType,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) => {
  return (props: any) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};